import * as React from 'react';
import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FluentProvider, webLightTheme, webDarkTheme, makeStyles, tokens, Divider } from '@fluentui/react-components';
import { ServiceContext } from '@msx/platform-services';
import { useMCAPSCopilotTelemetry } from './utils/mcapsCopilotTelemetry';
import MessageBody from './MessageBody';
import FooterMessageBox from './FooterMessageBox';
import FollowUpQuestions from './FollowUpQuestions';
import NewConversation from './NewConversation';
import { PromptGallery, PromptGalleryButton } from './PromptGallery';
import { PromptGalleryService } from './PromptGallery/services/promptGalleryService';

import { COPILOT_CONSTANTS, DEFAULT_VALUES } from './constants';
import { WelcomePrompt, SAMPLE_CONFIGURATIONS, replaceUserNamePlaceholder } from './Suggestion';
import type { SimpleCopilotPanelProps, Message } from './interfaces';
import type { SelfHelpFeedbackState } from './interfaces/selfHelpFeedback';

import { FocusTrapZone } from '@fluentui/react';
import { useAccessibility } from './hooks/useAccessibility';
import { useCopilotChat } from './hooks/useCopilotChat';
import type { CopilotChatParams } from './hooks/CopilotChatParams';
import CopilotErrorBoundary from './CopilotErrorBoundary';
import {
  abandonIncompleteSelfHelpFeedback,
  emitSelfHelpFeedbackJourneyEvent,
  hasRestorableSelfHelpFeedbackJourney,
  shouldRestoreSelfHelpFeedbackHistory,
} from './utils/selfHelpFeedbackSession';
import { attachSelfHelpFeedbackTelemetryListener } from './utils/selfHelpFeedbackTelemetry';


const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
    background: '#f3f2f1',
    overflow: 'hidden',
    '& [class*="disclaimer"]': { display: 'none !important' },
    '& [class*="ai-generated"]': { display: 'none !important' },
    '& [data-testid*="disclaimer"]': { display: 'none !important' }
  },
  chatWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '12px',
    background: '#f3f2f1'
  },
  footer: {
    background: '#f3f2f1',
    flexShrink: 0,
    flexGrow: 0,
    minHeight: 'auto',
    position: 'relative',
    zIndex: 10,
    padding: '4px 12px 8px'
  }
});
const SimpleCopilotPanel: React.FC<SimpleCopilotPanelProps> = ({
  apiUrl,
  userEmail = DEFAULT_VALUES.USER_EMAIL,
  userFullName,
  themeMode = DEFAULT_VALUES.THEME_MODE,
  showFooterBorder = false,
  initialPrompt,
  onInitialPromptApplied
}) => {
  const styles = useStyles();
  const platformContext = React.useContext(ServiceContext);
  const dispatch = useDispatch();
  const mcapsTelemetry = useMCAPSCopilotTelemetry(dispatch);

  // ========== State (orchestrator owns all state) ==========
  const [messages, setMessages] = useState<Message[]>([]);
  const [followUpQuestions, setFollowUpQuestions] = useState<Array<{ question: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [showChangeTopic, setShowChangeTopic] = useState(true);
  const [isPromptGalleryOpen, setIsPromptGalleryOpen] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentFormMetadata, setCurrentFormMetadata] = useState<any>(null);
  const [currentFormQuery, setCurrentFormQuery] = useState<string>('');
  const [persistentFormValues, setPersistentFormValues] = useState<Record<string, any>>({});
  const [currentActiveFormId, setCurrentActiveFormId] = useState<string | null>(null);
  const [submittedFormIds, setSubmittedFormIds] = useState<Set<string>>(new Set());
  const [currentTicketNumber, setCurrentTicketNumber] = useState<string | null>(null);
  const [appNameVersion, setAppNameVersion] = React.useState(0);

  // ========== Refs (orchestrator owns all refs) ==========
  const isAwaitingResetConfirmationRef = useRef<boolean>(false);
  const controlRef = useRef<any>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const lastAnchoredMessageIdRef = React.useRef<string | null>(null);
  const globalUploadedFilesRef = useRef<File[]>([]);
  const effectiveApi = React.useMemo(() => apiUrl?.trim(), [apiUrl]);
  const sessionIdRef = useRef<string>(
    COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2)
  );
  const hasCheckedPageNavigationRef = useRef(false);
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  React.useEffect(
    () => attachSelfHelpFeedbackTelemetryListener(() => messagesRef.current),
    []
  );

  // ========== Build params + call façade hook ==========
  const chatParams: CopilotChatParams = {
    messages, setMessages,
    loading, setLoading,
    followUpQuestions, setFollowUpQuestions,
    uploadedFiles, setUploadedFiles,
    currentFormMetadata, setCurrentFormMetadata,
    currentFormQuery, setCurrentFormQuery,
    currentActiveFormId, setCurrentActiveFormId,
    submittedFormIds, setSubmittedFormIds,
    persistentFormValues, setPersistentFormValues,
    currentTicketNumber, setCurrentTicketNumber,
    showExitConfirmation, setShowExitConfirmation,
    showChangeTopic, setShowChangeTopic,
    isPromptGalleryOpen, setIsPromptGalleryOpen,
    appNameVersion, setAppNameVersion,
    sessionIdRef, controlRef, scrollRef,
    lastAnchoredMessageIdRef, globalUploadedFilesRef,
    isAwaitingResetConfirmationRef,
    effectiveApi, userEmail, userFullName,
    mcapsTelemetry, dispatch,
    platformAuthClient: platformContext.authClient,
    platformContext,
    initialPrompt, onInitialPromptApplied,
  };

  const {
    clearConversation,
    handleFullClear,
    maintainHistory,
    handleSendMessage,
    handleSelfHelpTicketReady,
    handlePromptSelected,
    handleRemoveMessage,
    handleFormSubmit,
    handleAdaptiveCardAction,
    sendResetToBackend,
  } = useCopilotChat(chatParams);
  const fullClearHandlerRef = useRef(handleFullClear);
  fullClearHandlerRef.current = handleFullClear;

  const handleSelfHelpFeedbackStateChange = React.useCallback(
    (messageId: string, state: SelfHelpFeedbackState) => {
      setMessages(previousMessages => {
        let changed = false;
        const nextMessages = previousMessages.map(message => {
          if (
            message.id !== messageId ||
            message.meta?.selfHelpFeedbackState === state
          ) {
            return message;
          }

          changed = true;
          return {
            ...message,
            meta: {
              ...message.meta,
              selfHelpFeedbackState: state,
            },
          };
        });
        return changed ? nextMessages : previousMessages;
      });
    },
    []
  );

  const handleSelfHelpFeedbackDraftChange = React.useCallback(
    (messageId: string, draft: string) => {
      setMessages(previousMessages =>
        previousMessages.map(message =>
          message.id === messageId
            ? {
                ...message,
                meta: {
                  ...message.meta,
                  selfHelpFeedbackDraft: draft,
                },
              }
            : message
        )
      );
    },
    []
  );

  // ========== Orchestrator-level effects (not in domain hooks) ==========

  // Page navigation detection + history restoration (runs once on mount)
  React.useEffect(() => {
    const persisted = maintainHistory('GET');
    if (!hasCheckedPageNavigationRef.current) {
      hasCheckedPageNavigationRef.current = true;
      
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const navigationType = navigationEntries[0]?.type;
      const isPageNavigation =
        navigationType === 'reload' || navigationType === 'navigate';
      
      const hasActiveFeedback =
        hasRestorableSelfHelpFeedbackJourney(persisted?.messages);
      const shouldRestoreActiveFeedback =
        shouldRestoreSelfHelpFeedbackHistory(
          navigationType,
          persisted?.messages
        );

      if (
        navigationType === 'navigate' &&
        hasActiveFeedback &&
        Array.isArray(persisted?.messages)
      ) {
        messagesRef.current = persisted.messages;
        const abandoned = abandonIncompleteSelfHelpFeedback(
          persisted.messages,
          'navigation'
        );
        abandoned.abandoned.forEach(event =>
          emitSelfHelpFeedbackJourneyEvent({ type: 'abandoned', ...event })
        );
      }

      if (isPageNavigation && !shouldRestoreActiveFeedback) {
        sessionIdRef.current = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
        
        sessionStorage.removeItem('mcapsCopilot_ticketNumber');
        setCurrentTicketNumber(null);
        maintainHistory('RESET');

        // Restore backend reset dropped during extraction (verbatim from develop L236-260)
        sendResetToBackend(false);
        
        mcapsTelemetry.logComponentInteraction({
          action: 'click',
          component: 'SimpleCopilotPanel - Page Navigation Reset'
        });
        
        return; // Don't restore history on page navigation
      }
    }
    
    if (persisted && Array.isArray(persisted.messages)) {
      const filteredMessages = persisted.messages.filter((msg: Message) => {
        if (msg.role === 'separator' && msg.showSignInButton === true) {
          return false;
        }
        if ((msg.role === 'separator' || msg.role === 'system') && !msg.content) {
          return false;
        }
        return true;
      });
      
      setMessages(filteredMessages);
      if (Array.isArray(persisted.followUpQuestions)) {
        setFollowUpQuestions(persisted.followUpQuestions);
      }
      if (persisted.sessionId) {
        sessionIdRef.current = persisted.sessionId;
      }
      if (persisted.currentActiveFormId) {
        setCurrentActiveFormId(persisted.currentActiveFormId);
      }
      if (persisted.submittedFormIds) {
        setSubmittedFormIds(new Set(persisted.submittedFormIds));
      }
    }
    const savedTicketNumber = sessionStorage.getItem('mcapsCopilot_ticketNumber');
    if (savedTicketNumber) {
      setCurrentTicketNumber(savedTicketNumber);
    }
  }, [maintainHistory, mcapsTelemetry]);

  // Re-fetch prompt gallery data if tile context (appName) changed since last cache
  React.useEffect(() => {
    const refreshForTileContext = async () => {
      try {
        if (platformContext) {
          await PromptGalleryService.initializeGlobalPromptData(platformContext, { token: "consumption" });
        }
      } catch {
        // Non-fatal: prompt gallery will use cached or fallback data
      }
    };
    refreshForTileContext();
  }, [platformContext]);

  // Disclaimer removal
  React.useEffect(() => {
    const removeDisclaimers = () => {
      const disclaimerSelectors = [
        '[class*="disclaimer"]',
        '[class*="ai-generated"]',
        '[data-testid*="disclaimer"]'
      ];
      disclaimerSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el.textContent?.toLowerCase().includes('ai-generated') ||
              el.textContent?.toLowerCase().includes('may be incorrect') ||
              el.textContent?.toLowerCase().includes('disclaimer')) {
            (el as HTMLElement).style.display = 'none';
          }
        });
      });
    };
    removeDisclaimers();
    const timeoutId = setTimeout(removeDisclaimers, 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  // File upload global ref sync
  React.useEffect(() => {
    const handleFilesUpdated = (event: CustomEvent) => {
      const { files } = event.detail;
      globalUploadedFilesRef.current = files || [];
    };
    document.addEventListener('form-submitted', handleFilesUpdated);
    return () => {
      document.removeEventListener('form-submitted', handleFilesUpdated);
    };
  }, []);

  // WI-1249082: Listen for system messages from copilot UI (e.g., "Create an Incident Request" button)
  React.useEffect(() => {
    const handleSystemMessage = (event: CustomEvent) => {
      const { content } = event.detail || {};
      if (content) {
        setMessages(prev => {
          // If last message is already a system-ticket confirmation, replace it (don't append duplicates)
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.id.startsWith('system-ticket-')) {
            return [...prev.slice(0, -1), {
              id: 'system-ticket-confirmation',
              role: 'assistant' as const,
              content,
              timestamp: Date.now(),
              meta: { responseType: 'text' },
            }];
          }
          // Otherwise append as new message
          return [...prev, {
            id: 'system-ticket-confirmation',
            role: 'assistant' as const,
            content,
            timestamp: Date.now(),
            meta: { responseType: 'text' },
          }];
        });
      }
    };
    window.addEventListener('copilot-system-message', handleSystemMessage as EventListener);
    return () => window.removeEventListener('copilot-system-message', handleSystemMessage as EventListener);
  }, []);

  // WI-1248753: Listen for escalate-ticket event from MarkdownMessage LinkRenderer
  // Auto-sends "Escalate {ticketId}" as a user message to trigger the existing escalation flow
  React.useEffect(() => {
    const handleEscalateTicket = (e: CustomEvent<{ ticketId: string }>) => {
      const ticketId = e.detail?.ticketId;
      if (ticketId) {
        handleSendMessage(`Escalate ${ticketId}`);
      }
    };
    window.addEventListener('copilot-escalate-ticket', handleEscalateTicket as EventListener);
    return () => window.removeEventListener('copilot-escalate-ticket', handleEscalateTicket as EventListener);
  }, [handleSendMessage]);

  // ========== Accessibility (uses hook's clearConversation) ==========
  const { handleKeyDown } = useAccessibility(
    isPromptGalleryOpen,
    setIsPromptGalleryOpen,
    clearConversation
  );

  // ========== UI-only handlers (not in hooks) ==========
  const handleOpenPromptGallery = () => {
    setIsPromptGalleryOpen(true);
    mcapsTelemetry.logQuerySubmitted({
      query: 'Prompt Gallery Opened',
      component: 'SimpleCopilotPanel',
      action: 'click'
    });
  };

  const handleClosePromptGallery = () => {
    setIsPromptGalleryOpen(false);
  };

  const handleExitToggle = (checked?: boolean) => {
    if (checked === false || showExitConfirmation) {
      mcapsTelemetry.logExitConfirmation({
        action: "click",
        message: checked === false ? "Exit toggle turned off by user" : "Exit confirmation canceled"
      });
      
      setShowExitConfirmation(false);
      if (controlRef.current?.setInputText) {
        controlRef.current.setInputText("");
      }
    } else {
      mcapsTelemetry.logExitConfirmation({
        action: "click",
        message: "Exit confirmation dialog shown"
      });
      
      setShowExitConfirmation(true);
      if (controlRef.current?.setInputText) {
        controlRef.current.setInputText("Yes, I want to exit.");
      }
    }
  };

  const handlePromptGallerySelection = (prompt: string) => {
    mcapsTelemetry.logQuerySubmitted({
      query: `Prompt Gallery Selected: ${prompt}`,
      component: 'SimpleCopilotPanel',
      action: 'click'
    });
    if (controlRef.current?.setInputText) {
      controlRef.current.setInputText(prompt);
    }
  };

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files);
    globalUploadedFilesRef.current = files;
  };

  const handleFileRemove = (fileName: string) => {
    const newFiles = uploadedFiles.filter(file => file.name !== fileName);
    setUploadedFiles(newFiles);
    globalUploadedFilesRef.current = newFiles;
  };
  
  const lastMessage = messages[messages.length - 1];
  React.useEffect(() => {
    if (!lastMessage) return;

    // Keep welcome behavior unchanged
    if (messages.length === 1 && lastMessage.role === 'assistant' && lastMessage.id === 'welcome') {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
      return;
    }

    requestAnimationFrame(() => {
      const container = scrollRef.current;
      if (!container) {
        return;
      }

      // WI-1249082: Skip anchor scroll for synthetic system messages — scroll to bottom
      if (lastMessage.id.startsWith('system-')) {
        container.scrollTop = container.scrollHeight;
        return;
      }

      const anchorId = lastAnchoredMessageIdRef.current;

      // If we don't yet have an anchor question, default to bottom behaviour
      if (!anchorId) {
        container.scrollTop = container.scrollHeight;
        return;
      }

      const selector = `[data-message-id="${anchorId}"]`;
      const messageEl = container.querySelector<HTMLElement>(selector);

      if (!messageEl) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const messageRect = messageEl.getBoundingClientRect();
      const delta = messageRect.top - containerRect.top;

      // Scroll so the anchored user question becomes the first visible item
      container.scrollTop = container.scrollTop + delta;
    });
  }, [messages.length, lastMessage?.id]);

  const rootRef = useRef<HTMLDivElement | null>(null);

  
  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    
    const applyViewportFill = () => {
      const el = rootRef.current;
      if (!el) return;
      
      const rect = el.getBoundingClientRect();
      const available = window.innerHeight - rect.top;
      
      if (available > 0 && Math.abs(rect.height - available) > 2) {
        el.style.height = available + 'px';
      }
      document.documentElement.style.overflowY = 'hidden';
    };
    
    const throttledResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(applyViewportFill, 16); 
    };
    
    applyViewportFill();
    window.addEventListener('resize', throttledResize);
    
    return () => {
      window.removeEventListener('resize', throttledResize);
      clearTimeout(resizeTimeout);
      document.documentElement.style.overflowY = '';
    };
  }, []);

  
  React.useEffect(() => {
    const checkForRefreshTrigger = () => {
      const trigger = sessionStorage.getItem('mcapsCopilot_triggerFullClear');
      if (trigger) {
        sessionStorage.removeItem('mcapsCopilot_triggerFullClear');
        fullClearHandlerRef.current();
      }
    };

    // Reduced polling frequency for better performance
    const interval = setInterval(checkForRefreshTrigger, 250);
    
    return () => clearInterval(interval);
  }, []);

  
  // Enhanced switch disable logic with per-message granular control
  const getSwitchDisableState = React.useMemo(() => {
    // Find the latest assistant message (most recent API response)
    const latestAssistantMessage = messages
      .slice()
      .reverse()
      .find(message => message.role === 'assistant');
    
    if (!latestAssistantMessage) {
      return {
        disableAllSwitches: false,
        latestMessageId: null,
        latestFormSessionActive: false
      };
    }
    
    const meta = latestAssistantMessage.meta as any;
    const dataToDisplay = meta?.dataToDisplay as any;
    const latestFormSessionActive = meta?.formSessionActive || dataToDisplay?.formSessionActive || false;
    
    return {
      // Condition 1: If latest response has formSessionActive: false, disable ALL switches
      disableAllSwitches: !latestFormSessionActive,
      latestMessageId: latestAssistantMessage.id,
      latestFormSessionActive: latestFormSessionActive
    };
  }, [messages]);

  // Legacy compatibility: shouldDisableAllSwitches for condition 1 (disable all switches)
  const shouldDisableAllSwitches = getSwitchDisableState.disableAllSwitches;
  
  const selectedTheme = React.useMemo(() => 
    themeMode === 'dark' ? webDarkTheme : webLightTheme
  , [themeMode]);

  return (
    <FluentProvider theme={selectedTheme}>
      <CopilotErrorBoundary fallbackMessage="The copilot chat interface encountered an error and needs to be refreshed.">
        <FocusTrapZone
          isClickableOutsideFocusTrap={true}
          forceFocusInsideTrap={false}
        >
          <div 
            className={styles.root} 
            ref={rootRef}
            role="main"
            aria-label="MCAPSHelp Copilot Chat Interface"
          >
        
        {/* Messages area - scrollable */}
        <div 
          id="main-chat-content"
          className={styles.chatWrapper} 
          ref={scrollRef}
          role="region"
          aria-label="Chat conversation"
          tabIndex={0}
        >
          
          <WelcomePrompt 
            key={`welcome-${appNameVersion}`}
            showIcon={true}
            onSuggestionClick={handlePromptSelected}
            message={replaceUserNamePlaceholder(SAMPLE_CONFIGURATIONS.LEGACY_MAIN_JSX.botWelcomeMessage, userFullName)}
          />
          
          <MessageBody
            messages={messages}
            isLoading={loading}
            onFormSubmit={handleFormSubmit}
            submittedFormIds={submittedFormIds}
            //onShowNextSection={handleShowNextSection}
            userEmail={userEmail}
            apiUrl={effectiveApi}
            persistentFormValues={persistentFormValues}
            onPromptSelected={handlePromptSelected}
            onExitToggle={handleExitToggle}
            shouldDisableAllSwitches={shouldDisableAllSwitches}
            switchDisableState={getSwitchDisableState}
            mcapsTelemetry={mcapsTelemetry}
            onRemoveMessage={handleRemoveMessage}
            onAdaptiveCardAction={handleAdaptiveCardAction}
            onSelfHelpFeedbackStateChange={handleSelfHelpFeedbackStateChange}
            onSelfHelpFeedbackDraftChange={handleSelfHelpFeedbackDraftChange}
            onSelfHelpResolved={(context) =>
              emitSelfHelpFeedbackJourneyEvent({
                type: 'resolved',
                ...context,
              })
            }
            onSelfHelpTicketReady={handleSelfHelpTicketReady}
          />

          {/* Follow-up Questions */}
          {followUpQuestions.length > 0 && (
            <FollowUpQuestions
              followUpQuestions={followUpQuestions}
              handlePromptSelected={handlePromptSelected}
              dispatch={dispatch}
            />
          )}
        </div>

        {/* Hidden aria-live region for screen reader announcements */}
        <div
          id="global-aria-live"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        />

        {/* Chat Input - Fixed at bottom */}
        <div
          className={styles.footer}
          style={showFooterBorder ? { borderTop: `1px solid ${tokens.colorNeutralStroke2}` } : { borderTop: 'none' }}
        >
          {/* New Conversation Button - Positioned above input like legacy FooterChat.jsx */}
          <NewConversation 
            show={showChangeTopic}
            onNewConversation={clearConversation}
            text={COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION}
            theme={themeMode}
            dispatch={dispatch}
          />
          
          <FooterMessageBox
            controlRef={controlRef}
            handleSendMessage={handleSendMessage}
            placeholderText={COPILOT_CONSTANTS.MESSAGES.PLACEHOLDER}
            isLoading={loading}
            maxCount={COPILOT_CONSTANTS.INPUT_LIMITS.MAX_CHARACTER_COUNT}
            hasUploadedFiles={uploadedFiles.length > 0}
            uploadedFiles={uploadedFiles}
            onFileRemove={handleFileRemove}
            onFilesChange={handleFilesChange}
            additionalActions={
              <>
                <PromptGalleryButton
                  onOpenGallery={handleOpenPromptGallery}
                  disabled={loading}
                  dispatch={dispatch}
                />
                <Divider 
                  vertical 
                  style={{  margin: '0 4px' }} 
                />
              </>
            }
          />
        </div>

        {/* Prompt Gallery Modal */}
        <PromptGallery
          isOpen={isPromptGalleryOpen}
          onClose={handleClosePromptGallery}
          onPromptSelected={handlePromptGallerySelection}
          themeMode={themeMode}
          title="Copilot Prompt Gallery: MCAPSHelp Agent"
          dispatch={dispatch}
         
        />

          </div>
        </FocusTrapZone>
      </CopilotErrorBoundary>
    </FluentProvider>
  );
};

export default SimpleCopilotPanel;

// Generated by Copilot
// updated on: 2026-01-01 | 40+ lines added/updated | Feature Integration | Added Adaptive Card action handling with handleAdaptiveCardAction callback and proper API response parsing | Component Enhancement
// updated on: 2026-03-19 | 22 lines added | [Auto Generated] | Zero-deploy tile routing: added mcapsCopilot_appNameChanged event listener to re-render WelcomePrompt and invalidate prompt gallery cache when tile appName changes at runtime | Time Taken 10 min
// updated on: 2026-04-13 | 1084 lines removed | [Refactor] | God class refactoring Phase 1b: wired useCopilotChat facade hook, moved inline functions to domain hooks (useSessionManagement, useMessageSender, useFormHandler, useAdaptiveCardHandler) | Time Taken 30 min
// updated on: 2026-09-09 | Story 1297509 TASK-009 | Persisted feedback metadata and restored active journeys across reload | Time Taken 7 min
// updated on: 2026-09-09 | Story 1297509 TASK-010 | Attached one privacy-safe self-help journey telemetry listener | Time Taken 3 min
// Generated by Copilot
// updated on: 2026-10-26 | [Auto Generated] | Resolve second-stash conflict while retaining reset and completion history changes
// Generated by Copilot
// updated on: 2026-10-26 | 3 lines added | [Auto Generated] | Forward ordinary submitted form identities to history | Time Taken 1 min
